import React, { useEffect, useState, useCallback } from "react";
import { classNames } from "../../../../../helpers/classNames";
import ButtonDropdown from "../../../../ButtonDropdown";
import SmartTableSortsRow from "./SmartTable___Sorts___Row";
import { TbArrowsDownUp } from "react-icons/tb";
import { useMutation, useQueryClient } from "react-query";
import { ApiRequest, API_AUTH_TYPES, API_ROUTES } from "../../../../../api";
import areArraysIdentical from "../../../../../helpers/arrayArraysIdentical";
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

const viewsApi = new ApiRequest(
  API_ROUTES.SMART_TABLE_VIEWS,
  API_AUTH_TYPES.ADVISOR
);

const SmartTableSorts = ({ columns, dbSorts, viewId, tableId, onSortsChange }) => {
  const [open, setOpen] = useState(false);
  const [lastSavedSorts, setLastSavedSorts] = useState(dbSorts);

  useEffect(() => {
    setLastSavedSorts(dbSorts);
  }, [dbSorts]);

  const queryClient = useQueryClient();

  const { mutate: runSaveSorts, isLoading: isSaving } = useMutation(
    async (sortsToSave) => {
      try {
        await viewsApi.patch({
          endpoint: "update",
          body: {
            column: "sorts",
            value: sortsToSave,
            id: viewId
          }
        });

        onSortsChange(sortsToSave);

        // Optimistically update the cache to prevent reverts when cells are edited
        queryClient.setQueryData(["smart-table-complete-data", { tableId }], (oldData) => {
          if (oldData && oldData.view) {
            return {
              ...oldData,
              view: {
                ...oldData.view,
                sorts: sortsToSave
              }
            };
          }
          return oldData;
        });

      } catch (error) {
        console.error("Error saving sorts:", error);
      }
    },
    {
      onSuccess: (data, variables) => {
        setLastSavedSorts(variables);
      },
      onError: (error) => {
        console.error("Error saving sorts:", error);
      }
    }
  );

  const handleLocalSortsChangeAndSave = useCallback((newSortsArray) => {
    onSortsChange(newSortsArray);
    runSaveSorts(newSortsArray);
  }, [onSortsChange, runSaveSorts]);

  const moveSort = useCallback((dragIndex, hoverIndex) => {
    const newSorts = [...(dbSorts || [])];
    const draggedSort = newSorts[dragIndex];
    newSorts.splice(dragIndex, 1);
    newSorts.splice(hoverIndex, 0, draggedSort);
    handleLocalSortsChangeAndSave(newSorts);
  }, [dbSorts, handleLocalSortsChangeAndSave]);

  const handleAddCondition = () => {
    if (!columns || columns.length === 0) return;
    const firstSortableColumn = columns.find(col => !["button", "copy_to_clipboard"].includes(col.col_type));
    if (!firstSortableColumn) return;

    const newSort = {
      col_id: firstSortableColumn.col_id,
      direction: "asc",
    };
    const newSortsArray = [...(dbSorts || []), newSort];
    handleLocalSortsChangeAndSave(newSortsArray);
  };

  const changesMade = !areArraysIdentical(dbSorts, lastSavedSorts);

  if (!columns) {
    return (
      <button className="rounded text-gray-400 px-2 py-1 flex space-x-1 items-center" disabled>
        <TbArrowsDownUp className="w-6" />
        <p className="hidden xl:flex">Sort</p>
      </button>
    );
  }

  return (
    <div className="">
      <ButtonDropdown open={open} setOpen={setOpen}>
        <div className="p-2 min-w-[700px]">
          <div className="mb-2">
            <h3 className="text-base font-medium text-gray-900 mb-1">Sort Configuration</h3>
            <p className="text-sm text-gray-500">Numbers show priority order • Drag to reorder • Click + to add more conditions</p>
          </div>
          <DndProvider backend={HTML5Backend}>
            <div className="space-y-1">
              {(dbSorts || []).map((sort, index) => (
                <SmartTableSortsRow
                  key={`${sort.col_id}-${index}`}
                  allSorts={dbSorts || []}
                  onSortsChange={handleLocalSortsChangeAndSave}
                  columns={columns}
                  sort={sort}
                  thisSortIndex={index}
                  moveSort={moveSort}
                />
              ))}
            </div>
          </DndProvider>
          <div className="flex justify-between items-center mt-3 pt-2 border-t border-gray-200">
            <button
              onClick={handleAddCondition}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              disabled={isSaving}
            >
              + Add Sort Condition
            </button>

            <button
              onClick={() => runSaveSorts(dbSorts)}
              disabled={isSaving || !changesMade}
              className={classNames(
                'inline-flex items-center px-4 py-2 text-sm font-medium border rounded-md transition-colors duration-150',
                isSaving ? 'bg-gray-200 text-gray-500 cursor-not-allowed border-gray-200' :
                  changesMade ? 'bg-indigo-600 hover:bg-indigo-700 text-white border-indigo-600 animate-pulse' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-300'
              )}
            >
              {isSaving ? 'Saving...' : (changesMade ? 'Apply Changes' : 'Applied')}
            </button>
          </div>
        </div>
      </ButtonDropdown>
      <button
        onClick={() => setOpen(true)}
        className={classNames(
          "rounded hover:ring-2 hover:ring-gray-300 text-gray-600 px-2 py-1 flex space-x-1 items-center",
          (dbSorts && dbSorts.length > 0) && "bg-indigo-100",
          changesMade && "ring-2 ring-indigo-400 ring-offset-1"
        )}
      >
        <TbArrowsDownUp className="w-6 " />
        <p className="hidden xl:flex">
          {(dbSorts && dbSorts.length > 0)
            ? `Sorted by ${dbSorts.length} field${dbSorts.length > 1 ? "s" : ""}`
            : "Sort"}
          {changesMade && !isSaving && ' *'}
        </p>
      </button>
    </div>
  );
};

export default SmartTableSorts;
